import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import { useConfirm } from "@/context/ConfirmContext";
import { AHeader, ACard, inputDark, btnPrimary } from "@/components/shared/AdminKit";
import { PlatformIcon } from "@/components/shared/Badges";
import { toast } from "sonner";
import { useTr } from "@/lib/tr";
import { catalogError } from "@/lib/catalogError";
import { Loader2, Save, Trash2, Plus, GripVertical, Eye, EyeOff } from "lucide-react";

export default function AdminPlatforms() {
  const { t } = useContent();
  const tr = useTr();
  const confirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [nw, setNw] = useState({ name: "", name_en: "", icon: "globe" });
  const dragId = useRef(null);

  const load = () => api.get("/admin/platforms").then((r) => {
    setRows(r.data.map((p) => ({ ...p, active: p.active !== false && p.status !== "inactive" })));
    setLoading(false);
  });
  useEffect(() => { load(); }, []);

  const setField = (id, k, v) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [k]: v } : r)));

  const save = async (row) => {
    setSavingId(row.id);
    try {
      await api.put(`/admin/platforms/${row.id}`, { name: row.name, name_en: row.name_en, icon: row.icon, active: row.active });
      toast.success(t("plat.saved", "Plateforme enregistrée")); load();
    } catch (e) { toast.error(catalogError(e, tr, t("plat.save_error", "Échec de l'enregistrement"))); }
    finally { setSavingId(null); }
  };

  const toggleActive = async (row) => {
    const v = !row.active; setField(row.id, "active", v);
    try { await api.put(`/admin/platforms/${row.id}`, { active: v }); } catch { toast.error(t("plat.save_error", "Échec")); }
  };

  const del = async (row) => {
    if (!(await confirm({ title: t("plat.confirm_del_title", "Supprimer la plateforme"), description: t("plat.confirm_del", "Supprimer cette plateforme ?"), danger: true }))) return;
    await api.delete(`/admin/platforms/${row.id}`); toast.success(t("plat.deleted", "Plateforme supprimée")); load();
  };

  const create = async () => {
    if (!nw.name.trim()) return;
    setCreating(true);
    try {
      await api.post("/admin/platforms", { ...nw, sort_order: rows.length, active: true });
      setNw({ name: "", name_en: "", icon: "globe" }); toast.success(t("plat.created", "Plateforme créée")); load();
    } catch (e) { toast.error(catalogError(e, tr, t("plat.create_error", "Échec de la création"))); }
    finally { setCreating(false); }
  };

  const onDrop = async (targetId) => {
    const from = rows.findIndex((r) => r.id === dragId.current);
    const to = rows.findIndex((r) => r.id === targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...rows];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setRows(next);
    dragId.current = null;
    try { await api.post("/admin/platforms/reorder", { ids: next.map((r) => r.id) }); }
    catch { toast.error(t("plat.reorder_error", "Échec du réordonnancement")); }
  };

  return (
    <div>
      <AHeader title={t("plat.title", "Plateformes")} subtitle={t("plat.subtitle", "Créez, renommez (FR/EN), changez l'icône, réordonnez par glisser-déposer et activez/désactivez.")} />

      <ACard className="mb-4">
        <div className="flex flex-wrap items-end gap-2">
          <div><label className="text-xs text-slate-400">{t("plat.col.fr", "Nom (FR)")}</label><input value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} data-testid="new-platform-name" className={inputDark} placeholder="Ex: YouTube" /></div>
          <div><label className="text-xs text-slate-400">{t("plat.col.en", "Nom (EN)")}</label><input value={nw.name_en} onChange={(e) => setNw({ ...nw, name_en: e.target.value })} data-testid="new-platform-name-en" className={inputDark} placeholder="Ex: YouTube" /></div>
          <div><label className="text-xs text-slate-400">{t("plat.col.icon", "Icône")}</label><input value={nw.icon} onChange={(e) => setNw({ ...nw, icon: e.target.value })} data-testid="new-platform-icon" className={inputDark} placeholder="globe" /></div>
          <button onClick={create} disabled={creating} data-testid="create-platform-btn" className={btnPrimary}>{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {t("plat.create", "Ajouter")}</button>
        </div>
      </ACard>

      {loading ? <div className="py-10 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-accent" /></div> : (
        <ACard>
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr><th className="px-2 py-2.5"></th><th className="px-3 py-2.5">{t("plat.col.icon", "Icône")}</th><th className="px-3 py-2.5">{t("plat.col.fr", "Nom (FR)")}</th><th className="px-3 py-2.5">{t("plat.col.en", "Nom (EN)")}</th><th className="px-3 py-2.5">{t("plat.col.active", "Actif")}</th><th className="px-3 py-2.5 text-right">{t("plat.col.actions", "Actions")}</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} draggable onDragStart={() => (dragId.current = r.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(r.id)}
                  className="border-b border-slate-800/60 last:border-0" data-testid={`platform-row-${r.id}`}>
                  <td className="px-2 py-2.5 cursor-grab text-slate-500"><GripVertical className="h-4 w-4" /></td>
                  <td className="px-3 py-2.5"><div className="flex items-center gap-2"><PlatformIcon icon={r.icon} className="h-4 w-4 text-slate-200" /><input value={r.icon || ""} onChange={(e) => setField(r.id, "icon", e.target.value)} data-testid={`platform-icon-${r.id}`} className={`${inputDark} w-24`} /></div></td>
                  <td className="px-3 py-2.5"><input value={r.name || ""} onChange={(e) => setField(r.id, "name", e.target.value)} data-testid={`platform-fr-${r.id}`} className={inputDark} /></td>
                  <td className="px-3 py-2.5"><input value={r.name_en || ""} onChange={(e) => setField(r.id, "name_en", e.target.value)} data-testid={`platform-en-${r.id}`} className={inputDark} /></td>
                  <td className="px-3 py-2.5">
                    <button onClick={() => toggleActive(r)} data-testid={`platform-active-${r.id}`} className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium ${r.active ? "bg-emerald-500/20 text-emerald-400" : "bg-slate-700 text-slate-400"}`}>{r.active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />} {r.active ? t("plat.on", "Actif") : t("plat.off", "Inactif")}</button>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => save(r)} disabled={savingId === r.id} data-testid={`platform-save-${r.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50">{savingId === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {t("plat.save", "Enregistrer")}</button>
                      <button onClick={() => del(r)} data-testid={`platform-del-${r.id}`} className="text-red-400"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="px-3 py-10 text-center text-slate-500">{t("plat.empty", "Aucune plateforme.")}</td></tr>}
            </tbody>
          </table>
        </ACard>
      )}
    </div>
  );
}
